/**
 * A full tint ramp (50..950) from the one accent colour an operator picks.
 *
 * WHY
 * ---
 * The Branding screen gives us one colour, but the UI needs ten: a 50 for a
 * selected row, a 600 for a link, a 400 for the same link on a dark surface,
 * a 500/15 for a focus ring. Before this, only the primary button followed the
 * operator's colour and every link, tab underline and focus ring stayed the
 * blue compiled into the bundle -- so a platform branded green had green
 * buttons beside blue links, which reads as two products.
 *
 * HOW
 * ---
 * In OKLCH rather than HSL. HSL "lightness" is not what the eye sees: a yellow
 * and a blue at the same HSL lightness differ by a factor of three in
 * contrast, so an HSL ramp built from a yellow accent produced brand-600 link
 * text that was unreadable on white. OKLCH lightness IS perceptual, so pinning
 * each step to a fixed lightness gives every accent the same contrast at the
 * same step -- brand-600 on white is legible whatever colour was chosen.
 *
 * Hue comes from the accent; chroma is scaled from the accent's own chroma
 * (a grey accent yields a grey ramp) and pulled back into sRGB gamut where a
 * step would otherwise clip.
 *
 * Pure and dependency-free so it can run before React mounts.
 */

export const RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type RampStep = (typeof RAMP_STEPS)[number];

// Perceptual lightness per step, close to Tailwind's own blue so that the
// shipped default (#2563eb) reproduces the ramp the UI was designed against.
const L: Record<RampStep, number> = {
  50: 0.97, 100: 0.932, 200: 0.882, 300: 0.809, 400: 0.707, 500: 0.623,
  600: 0.546, 700: 0.488, 800: 0.424, 900: 0.379, 950: 0.282,
};

// Chroma relative to the accent's own. Tints near white and shades near black
// carry less colour, as they do in every hand-tuned palette.
const C: Record<RampStep, number> = {
  50: 0.07, 100: 0.15, 200: 0.27, 300: 0.45, 400: 0.76, 500: 0.98,
  600: 1, 700: 1, 800: 0.9, 900: 0.68, 950: 0.42,
};

type RGB = [number, number, number];

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function rgbToOklch([r, g, b]: RGB): [number, number, number] {
  const lr = srgbToLinear(r / 255);
  const lg = srgbToLinear(g / 255);
  const lb = srgbToLinear(b / 255);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L_ = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L_, Math.hypot(a, bb), Math.atan2(bb, a)];
}

/** Linear sRGB for an OKLCH colour; may fall outside [0, 1]. */
function oklchToLinear(L_: number, c: number, h: number): RGB {
  const a = c * Math.cos(h);
  const b = c * Math.sin(h);
  const l = (L_ + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L_ - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L_ - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function inGamut(rgb: RGB): boolean {
  return rgb.every((v) => v >= -0.0001 && v <= 1.0001);
}

function toTriplet(lin: RGB): string {
  return lin
    .map((v) => Math.round(Math.min(1, Math.max(0, linearToSrgb(Math.min(1, Math.max(0, v))))) * 255))
    .join(' ');
}

/** `#rgb` / `#rrggbb` to an RGB tuple, or null. */
export function parseHex(value: string): RGB | null {
  const hex = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16)) as RGB;
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;
  }
  return null;
}

/**
 * Space-separated `r g b` triplets for every step, ready for
 * `rgb(var(--brand-600) / <alpha>)`.
 */
export function accentRamp(rgb: RGB): Record<RampStep, string> {
  const [, chroma, hue] = rgbToOklch(rgb);
  const out = {} as Record<RampStep, string>;
  for (const step of RAMP_STEPS) {
    let c = chroma * C[step];
    let lin = oklchToLinear(L[step], c, hue);
    // Pull chroma back until the colour exists in sRGB. Twelve halvings is
    // well below one 8-bit step.
    if (!inGamut(lin)) {
      let lo = 0;
      let hi = c;
      for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        if (inGamut(oklchToLinear(L[step], mid, hue))) lo = mid;
        else hi = mid;
      }
      c = lo;
      lin = oklchToLinear(L[step], c, hue);
    }
    out[step] = toTriplet(lin);
  }
  return out;
}

/** The `r g b` triplet of a colour, for `rgb(var(--accent-rgb) / <alpha>)`. */
export function rgbTriplet(rgb: RGB): string {
  return rgb.join(' ');
}
